import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@mondivago/supabase';
import type { PartnerRow } from '@mondivago/types';

// ─── RPC Result Types ──────────────────────────────────────────────────────

/**
 * Raw result shape from the `resolve_partner_agent` RPC.
 * The RPC queries from `partner_workers` + `partners`.
 */
interface ResolvePartnerAgentResult {
  agent_id: string;
  partner_id: string;
  partner_name: string;
  agent_name: string;
  is_active: boolean;
}

/**
 * Resolved agent info returned by the `resolve_partner_agent` RPC.
 *
 * NOTE: The RPC now queries from `partner_workers` + `partners`
 * (since the old `partners` + `partner_workers` tables were merged).
 * Field names retain backward-compatible aliases for existing pages.
 */
export type ResolvedPartnerAgent = ResolvePartnerAgentResult;

/**
 * Raw result shape from the `create_partner_profile` RPC.
 */
interface CreatePartnerProfileResult {
  partner: PartnerRow;
  agent: {
    id: string;
    user_id: string;
    partner_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

// ─── Partner Agent ──────────────────────────────────────────────────────────

/**
 * usePartner
 *
 * Fetches the partner agent record (via RPC) for the currently authenticated user.
 * Returns `null` if the user is not registered as a partner agent.
 * Refetches when `userId` changes.
 */
export function usePartner(userId: string | undefined) {
  const [resolved, setResolved] = useState<ResolvedPartnerAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setResolved(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPartner = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('resolve_partner_agent', {
          p_user_id: userId,
        });

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setResolved(null);
        } else {
          // RPC returns TABLE → Supabase gives an array; extract first row
          const raw = data as unknown as ResolvePartnerAgentResult[];
          const row = Array.isArray(raw) ? (raw[0] ?? null) : null;
          setResolved(row);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch partner info');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchPartner();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { resolved, loading, error };
}

/**
 * Formats Supabase/API errors into user-readable messages.
 */
function formatPartnerError(err: unknown): string {
  if (!(err instanceof Error)) {
    return 'Error creating partner profile.';
  }

  const msg = err.message;

  if (msg.includes('23503') || msg.includes('foreign key constraint') || msg.includes('Key is not present in table')) {
    return 'Could not create partner profile: user profile not found. Contact technical support.';
  }

  if (msg.includes('23505') || msg.includes('unique constraint') || msg.includes('duplicate key')) {
    return 'A partner profile already exists for this account.';
  }

  if (msg.includes('42501') || msg.includes('permission denied') || msg.includes('policy')) {
    return 'Insufficient permissions. Contact the administrator.';
  }

  return `Error creating partner profile: ${msg}`;
}

/**
 * useCreatePartner
 *
 * Creates a walk-in partner partner record AND its primary agent
 * in a single atomic RPC call (`create_partner_profile`).
 */
export function useCreatePartner() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (params: {
      userId: string;
      companyName: string;
      contactEmail: string;
      contactPhone?: string | null;
      vatNumber?: string | null;
      billingAddress?: string | null;
      firstName: string;
      lastName: string;
    }): Promise<CreatePartnerProfileResult | null> => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: rpcError } = await supabase.rpc('create_partner_profile', {
          p_company_name: params.companyName,
          p_contact_email: params.contactEmail,
          p_contact_phone: params.contactPhone || null,
          p_vat_number: params.vatNumber || null,
          p_billing_address: params.billingAddress || null,
          p_agent_first_name: params.firstName,
          p_agent_last_name: params.lastName,
        });

        if (rpcError) throw rpcError;
        if (!data) throw new Error('No data returned from create_partner_profile RPC.');

        const result = data as unknown as CreatePartnerProfileResult;

        await supabase.auth
          .updateUser({
            data: {
              role: 'partner',
              is_partner: true,
            },
          })
          .catch((metaErr) => {
            logger.warn('[useCreatePartner] Failed to update user metadata:', metaErr);
          });

        return result;
      } catch (err) {
        logger.error('[useCreatePartner] Error:', err);
        const message = formatPartnerError(err);
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { create, loading, error };
}

// ─── Partner Profile (partner record) ──────────────────────────────────────

/**
 * usePartnerProfile
 *
 * Fetches the partner partner record for the currently authenticated user.
 */
export function usePartnerProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<PartnerRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: queryError } = await supabase
          .from('partners')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (cancelled) return;

        if (queryError) {
          setError(queryError.message);
        } else {
          setProfile(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch partner profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const updateProfile = useCallback(
    async (
      updates: Partial<
        Pick<
          PartnerRow,
          | 'company_name'
          | 'vat_number'
          | 'billing_address'
          | 'ref_code'
          | 'bank_iban'
          | 'bank_account_holder_name'
          | 'bank_swift_bic'
          | 'bank_name'
          | 'bank_country_iso2'
        >
      >
    ) => {
      if (!userId) return { success: false, error: 'Not authenticated' };

      setLoading(true);
      setError(null);

      try {
        const { data, error: dbError } = await supabase
          .from('partners')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('user_id', userId)
          .select()
          .maybeSingle();

        if (dbError) throw dbError;

        if (data) setProfile(data as PartnerRow);
        return { success: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error saving partner profile.';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  return { profile, loading, error, updateProfile };
}

// ─── Partner Bookings ──────────────────────────────────────────────────────

/** Raw shape from the bookings + tours join query. */
import type { PartnerBooking, Database } from '@mondivago/types';

type PartnerBookingRow = Database['public']['Tables']['bookings']['Row'] & {
  tours: { title: string; photos: string[] | null } | null;
};

import type { PartnerBooking } from '@mondivago/types';

// Re-export for backward compatibility via barrel
export type { PartnerBooking };

export function usePartnerBookings(partnerId: string | undefined) {
  const [bookings, setBookings] = useState<PartnerBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    if (!partnerId) {
      setBookings([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('bookings')
        .select(
          `
          id,
          total_price,
          platform_fee,
          partner_commission,
          status,
          created_at,
          tour_date,
          number_of_people,
          metadata,
          tours:tour_id(title, photos)
        `
        )
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const raw = (data ?? []) as unknown as PartnerBookingRow[];

      const mapped: PartnerBooking[] = raw.map((b) => ({
        id: b.id,
        tour_title: b.tours?.title || '',
        traveler_name: (b.metadata?.traveler_name as string) || (b.metadata?.fullName as string) || 'Traveler',
        total_price: Number(b.total_price) || 0,
        platform_fee: Number(b.platform_fee) || 0,
        partner_commission: Number(b.partner_commission) || 0,
        status: b.status,
        created_at: b.created_at,
        tour_date: b.tour_date,
        number_of_people: Number(b.number_of_people) || 1,
        metadata: b.metadata,
        tour_image: Array.isArray(b.tours?.photos) && b.tours.photos.length > 0 ? b.tours.photos[0] : null,
      }));

      setBookings(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading partner bookings.');
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void fetchBookings();
  }, [fetchBookings]);

  return { bookings, loading, error, refresh: fetchBookings };
}

// ─── Partner Balance ───────────────────────────────────────────────────────

import type { PartnerBalance } from '@mondivago/types';

export type { PartnerBalance };

export function usePartnerBalance(userId: string | undefined) {
  const [balance, setBalance] = useState<PartnerBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setBalance(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('partner_balances')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (dbError) throw dbError;

      if (data) {
        const raw = data as PartnerBalance;
        setBalance({
          ...raw,
          total_earned: Number(raw.total_earned) || 0,
          total_paid_out: Number(raw.total_paid_out) || 0,
          current_balance: Number(raw.current_balance) || 0,
          active_referrals: Number(raw.active_referrals) || 0,
          total_clicks: Number(raw.total_clicks) || 0,
          total_links: Number(raw.total_links) || 0,
        });
      } else {
        setBalance(null);
      }
    } catch (err) {
      setError('Error loading partner stats. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void fetchBalance();
  }, [fetchBalance]);

  const requestPayout = useCallback(async (amount: number) => {
    try {
      const { error } = await supabase.rpc('request_payout', {
        p_amount: amount,
      });

      if (error) {
        return { success: false, error: error.message || 'Error processing payout request' };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unable to process the payout. Try again later.',
      };
    }
  }, []);

  return { balance, loading, error, refresh: fetchBalance, requestPayout };
}

// ─── Partner Ledger ────────────────────────────────────────────────────────

/** Raw shape from the finance_ledger + bookings + tours join query. */
type PartnerLedgerRow = Database['public']['Tables']['finance_ledger']['Row'] & {
  booking: {
    total_price: number | null;
    tour_date: string | null;
    number_of_people: number | null;
    is_manual: boolean;
    metadata: Record<string, unknown> | null;
    tours: { title: string; photos: string[] | null } | null;
  } | null;
};

import type { PartnerLedgerEntry } from '@mondivago/types';
import { logger } from '@/shared/lib/logger';

export type { PartnerLedgerEntry };

export function usePartnerLedger(partnerId: string | undefined) {
  const [ledgerEntries, setLedgerEntries] = useState<PartnerLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLedger = useCallback(async () => {
    if (!partnerId) {
      setLedgerEntries([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('finance_ledger')
        .select(
          `
          id,
          booking_id,
          entry_type,
          amount,
          description,
          created_at,
          booking:booking_id (
            total_price,
            tour_date,
            number_of_people,
            is_manual,
            metadata,
            tours:tour_id (
              title,
              photos
            )
          )
        `
        )
        .filter('metadata->>partner_id', 'eq', partnerId)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;

      const raw = (data ?? []) as unknown as PartnerLedgerRow[];

      const mapped: PartnerLedgerEntry[] = raw.map((entry) => ({
        ...entry,
        amount: Number(entry.amount) || 0,
        booking: entry.booking
          ? {
              ...entry.booking,
              total_price: Number(entry.booking.total_price) || 0,
              number_of_people: Number(entry.booking.number_of_people) || 0,
              traveler_name:
                (entry.booking.metadata?.traveler_name as string) ||
                (entry.booking.metadata?.fullName as string) ||
                null,
            }
          : null,
      }));

      setLedgerEntries(mapped);
    } catch (err) {
      setError('Error loading partner earnings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void fetchLedger();
  }, [fetchLedger]);

  return { ledgerEntries, loading, error, refresh: fetchLedger };
}
